export const PRODUCT_TYPE_ENUM = {
  TRADITIONNEL: "Traditionnel",
  MAISON: "Maison",
  NON_MAISON: "Non-maison",
  FUMAISON: "Fumaison",
  PLATEAU_FRUITS_DE_MER: "Plateau FDM",
  INDUSTRIEL: "Industriel",
  // Poisson frais emballé : créé comme un produit LS (idProduitLS sur codeMagasin),
  // mais sa composition reste éditable comme un produit traditionnel.
  PRODUIT_LS: "Produit LS"
};

// Couleurs Vuetify par type de produit : le socle les ramène en pilule douce sur les puces,
// et la classe du même nom sert de fond au liseré de la ligne de validation.
export const PRODUCT_TYPE_COLORS = {
  [PRODUCT_TYPE_ENUM.TRADITIONNEL]: "blue-grey",
  [PRODUCT_TYPE_ENUM.MAISON]: "indigo",
  [PRODUCT_TYPE_ENUM.NON_MAISON]: "brown",
  [PRODUCT_TYPE_ENUM.FUMAISON]: "grey",
  [PRODUCT_TYPE_ENUM.PLATEAU_FRUITS_DE_MER]: "blue",
  [PRODUCT_TYPE_ENUM.INDUSTRIEL]: "green",
  [PRODUCT_TYPE_ENUM.PRODUIT_LS]: "teal"
};

export const getProductTypeColor = (productType) => {
  return PRODUCT_TYPE_COLORS[productType] || PRODUCT_TYPE_COLORS[PRODUCT_TYPE_ENUM.TRADITIONNEL];
};

export const getProductTypeIcon = (productType) => {
  // Ligatures Material : le parser mélangeait deux familles d'icônes,
  // celle-ci est celle du reste de l'application.
  const icons = {
    [PRODUCT_TYPE_ENUM.TRADITIONNEL]: "set_meal",
    [PRODUCT_TYPE_ENUM.MAISON]: "home",
    [PRODUCT_TYPE_ENUM.NON_MAISON]: "storefront",
    [PRODUCT_TYPE_ENUM.FUMAISON]: "outdoor_grill",
    [PRODUCT_TYPE_ENUM.PLATEAU_FRUITS_DE_MER]: "restaurant",
    [PRODUCT_TYPE_ENUM.INDUSTRIEL]: "factory",
    [PRODUCT_TYPE_ENUM.PRODUIT_LS]: "inventory_2"
  };
  return icons[productType] || "sell";
};

export const PRODUCT_TYPES = Object.values(PRODUCT_TYPE_ENUM);

// Couleur par famille de métadonnée : celles du thème Vuetify (main.js), communes au clavier poisson
export const METADATA_FIELD_COLORS = {
  idConditionnement: 'conditionnement',
  idPoisson: 'fish',
  idTaille: 'taille',
  idPreparation: 'preparation',
  idQualite: 'qualite',
  idOrigine: 'origin',
  idLabel: 'label'
};

export const getMetadataFieldColor = (fieldKey) => {
  return METADATA_FIELD_COLORS[fieldKey] || 'grey';
};

// Confidence level thresholds and colors
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.8,
  MEDIUM: 0.4
};

// Niveaux de confiance : les couleurs d'état du thème (alignées sur les tokens success / warning / danger)
export const CONFIDENCE_COLORS = {
  high: 'success',
  medium: 'warning',
  low: 'error'
};

export const getConfidenceLevel = (score) => {
  if (score >= CONFIDENCE_THRESHOLDS.HIGH) return 'high';
  if (score >= CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium';
  return 'low';
};

export const getConfidenceColor = (score) => {
  return CONFIDENCE_COLORS[getConfidenceLevel(score)];
};

// Migration map for old product type names to new ones
const OLD_TO_NEW_PRODUCT_TYPE_MAP = {
  "Produit traditionnel": PRODUCT_TYPE_ENUM.TRADITIONNEL,
  "Préparation maison": PRODUCT_TYPE_ENUM.MAISON,
  "Préparation non-maison": PRODUCT_TYPE_ENUM.NON_MAISON,
  "Fumaison": PRODUCT_TYPE_ENUM.FUMAISON,
  "Plateau de fruits de mer": PRODUCT_TYPE_ENUM.PLATEAU_FRUITS_DE_MER,
  "Produit industriel (Vin, conserve, ...)": PRODUCT_TYPE_ENUM.INDUSTRIEL
};

const NEW_TO_OLD_PRODUCT_TYPE_MAP = Object.fromEntries(
  Object.entries(OLD_TO_NEW_PRODUCT_TYPE_MAP).map(([old, new_]) => [new_, old])
);

export const denormalizeProductType = (productType) => {
  return NEW_TO_OLD_PRODUCT_TYPE_MAP[productType] || productType;
};

/**
 * Normalizes old product type names to new ones
 * @param {string} productType - Product type string (old or new)
 * @returns {string} Normalized product type
 */
export const normalizeProductType = (productType) => {
  if (!productType) return PRODUCT_TYPE_ENUM.TRADITIONNEL;
  
  // If it's an old value, map it to new
  if (OLD_TO_NEW_PRODUCT_TYPE_MAP[productType]) {
    return OLD_TO_NEW_PRODUCT_TYPE_MAP[productType];
  }
  
  // If it's already a new value, return as is
  if (Object.values(PRODUCT_TYPE_ENUM).includes(productType)) {
    return productType;
  }
  
  // Fallback to traditional
  return PRODUCT_TYPE_ENUM.TRADITIONNEL;
};
